import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, StatusBar, Dimensions, ScrollView, Alert, Modal, Animated } from 'react-native';
import { WebView } from 'react-native-webview';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';

const screenWidth = Dimensions.get('window').width;
const screenHeight = Dimensions.get('window').height;

const TradingViewChart = ({ isDarkMode }) => {
  const html = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">
        <style>
          body { 
            margin: 0; 
            padding: 0; 
            background: ${isDarkMode ? '#0F0F0F' : '#FFFFFF'}; 
            overflow: hidden; 
          }
          .tradingview-widget-container { 
            height: 100vh; 
            width: 100%; 
          }
          .tradingview-widget-container__widget { 
            height: calc(100% - 32px); 
            width: 100%; 
          }
          .tradingview-widget-copyright { 
            font-size: 10px; 
            text-align: center; 
            padding: 8px; 
            background: ${isDarkMode ? '#0F0F0F' : '#FFFFFF'}; 
            color: ${isDarkMode ? '#666' : '#999'}; 
          }
          .blue-text { 
            color: #2962FF; 
            text-decoration: none; 
          }
          .trademark { 
            color: ${isDarkMode ? '#666' : '#999'}; 
          }
        </style>
      </head>
      <body>
        <div class="tradingview-widget-container" style="height:100%;width:100%">
          <div class="tradingview-widget-container__widget" style="height:100%;width:100%"></div>
          <script type="text/javascript" src="https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js" async>
          {
            "allow_symbol_change": true,
            "calendar": false,
            "details": false,
            "hide_side_toolbar": true,
            "hide_top_toolbar": false,
            "hide_legend": false,
            "hide_volume": false,
            "hotlist": false,
            "interval": "1",
            "locale": "en",
            "save_image": true,
            "style": "1",
            "symbol": "OANDA:XAUUSD",
            "theme": "${isDarkMode ? 'dark' : 'light'}",
            "timezone": "Asia/Kolkata",
            "backgroundColor": "#0F0F0F",
            "gridColor": "rgba(242, 242, 242, 0.06)",
            "watchlist": [],
            "withdateranges": false,
            "compareSymbols": [],
            "studies": [],
            "autosize": true
          }
          </script>
        </div>
      </body>
    </html>`;
    
  return <WebView 
    style={{ flex: 1, borderWidth: 0, borderColor: 'transparent' }} 
    source={{ html }} 
    javaScriptEnabled={true}
    domStorageEnabled={true}
    startInLoadingState={true}
    scalesPageToFit={false}
    bounces={false}
    scrollEnabled={false}
    overScrollMode="never"
    allowsInlineMediaPlaybook={true}
    mixedContentMode="compatibility"
    onLoad={() => console.log('Advanced TradingView Chart loaded')}
    onError={(error) => console.log('Chart error:', error)}
  />;
};

const ChartsScreen = () => {
  const { isDarkMode, colors } = useTheme();
  const [drawerVisible, setDrawerVisible] = useState(false);
  
  const DRAWER_WIDTH = 0.78;
  const slideAnim = useRef(
    new Animated.Value(-screenWidth * DRAWER_WIDTH)
  ).current;

  const toggleDrawer = () => {
    Animated.timing(slideAnim, {
      toValue: drawerVisible ? -screenWidth * DRAWER_WIDTH : 0,
      duration: 280,
      useNativeDriver: true,
    }).start(() => setDrawerVisible(!drawerVisible));
  };

  const showReadOnlyAlert = () => {
    Alert.alert(
      'Read Only',
      'This feature is read only',
      [{ text: 'OK', onPress: () => {} }],
      { cancelable: true }
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Top Toolbar */}
      <View style={[styles.topToolbar, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
        <TouchableOpacity style={styles.toolbarButton} onPress={toggleDrawer}><Icon name="menu" size={20} color={colors.primary} /></TouchableOpacity>
        <TouchableOpacity style={styles.toolbarButton} onPress={showReadOnlyAlert}><Icon name="add" size={20} color={colors.primary} /></TouchableOpacity>
        <TouchableOpacity style={styles.toolbarButton} onPress={showReadOnlyAlert}><Icon name="chart-line-variant" size={20} color={colors.primary} /></TouchableOpacity>
        <TouchableOpacity style={styles.toolbarButton} onPress={showReadOnlyAlert}><Icon name="animation-outline" size={20} color={colors.primary} /></TouchableOpacity>
        <TouchableOpacity style={styles.toolbarButton} onPress={showReadOnlyAlert}><Icon name="notifications-outline" size={20} color={colors.primary} /></TouchableOpacity>
      </View>

      {/* Main Chart */}
      <View style={[styles.chartContainer, { backgroundColor: colors.background }]}>
        <TradingViewChart isDarkMode={isDarkMode} />
      </View>

      {/* Drawer */}
      <Modal visible={drawerVisible} transparent animationType="none">
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={toggleDrawer}>
          <Animated.View
            style={[styles.drawer, { transform: [{ translateX: slideAnim }] }]}
          >
            <View style={[styles.drawerHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.drawerTitle, { color: colors.text }]}>Menu</Text>
            </View>
            <View style={styles.drawerContent}>
              <Text style={[styles.drawerText, { color: colors.textSecondary }]}>Read only mode</Text>
            </View>
          </Animated.View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default ChartsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
    borderWidth: 0,
    borderColor: 'transparent',
  },
  topToolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderBottomWidth: 1,
    borderBottomColor: '#1F2937',
  },
  toolbarButton: { padding: 8 },
  symbolHeader: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderBottomWidth: 1,
    borderBottomColor: '#1F2937',
    borderWidth: 0,
    borderColor: 'transparent',
    display: 'none',
  },
  symbolTitleRow: {
    marginBottom: 8,
    display: 'none',
  },
  symbolInfo: {
    flex: 1,
    display: 'none',
  },
  symbolText: { 
    color: '#FFFFFF', 
    fontSize: 15, 
    fontWeight: '700',
    marginBottom: 2,
    display: 'none',
  },
  symbolSubtext: { 
    color: '#6B7280', 
    fontSize: 11,
    display: 'none',
  },
  timeframeRow: {
    marginBottom: 8,
  },
  timeframeScrollView: {
    flexGrow: 0,
  },
  timeframeContent: {
    paddingVertical: 2,
  },
  timeframeButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginRight: 6,
    borderRadius: 6,
    backgroundColor: 'transparent',
    minWidth: 32,
    alignItems: 'center',
  },
  timeframeButtonActive: {
    shadowColor: '#4A90E2',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  timeframeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  priceDataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 2,
  },
  ohlcContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    flexWrap: 'wrap',
  },
  ohlcGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    marginBottom: 2,
  },
  ohlcLabel: {
    fontSize: 11,
    fontWeight: '600',
    marginRight: 3,
  },
  ohlcValue: {
    fontSize: 11,
    fontWeight: '700',
  },
  rightDataContainer: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginLeft: 8,
  },
  changeText: {
    fontSize: 10,
    fontWeight: '700',
    marginBottom: 2,
    textAlign: 'right',
  },
  volumeText: {
    fontSize: 9,
    fontWeight: '500',
    textAlign: 'right',
  },
  volumeValue: {
    fontSize: 10,
    fontWeight: '700',
  },
  chartContainer: {
    flex: 1,
    backgroundColor: '#000000',
    borderWidth: 0,
    borderColor: 'transparent',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  drawer: {
    width: screenWidth * 0.78,
    height: '100%',
    backgroundColor: '#0B0F14',
    borderRightWidth: 1,
    borderRightColor: '#1F2937',
  },
  drawerHeader: {
    paddingTop: 60,
    paddingBottom: 18,
    paddingHorizontal: 18,
    borderBottomWidth: 1,
  },
  drawerTitle: {
    fontSize: 20,
    fontWeight: '600',
  },
  drawerContent: {
    padding: 18,
  },
  drawerText: {
    fontSize: 14,
  },
});
